﻿/** @module csi/printUtil */
define([
	"jquery",
	"imagesloaded",
	"dojo/aspect",
	"dojo/dom-style",
	"dojo/topic",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/api",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/sessionConstants",
	"csi/service/sessionService",
	"csi/state",
	"csi/state/rootStore",
	"csi/utility/htmlUtility",
	"dojo/text!csi/templates/printHeader.html"
], function(jquery, imagesloaded, aspect, domStyle, topic, Deferred, _, api, csiHeading, html, locale, sessionConstants, {getLogo}, state, {sessionStore}, {purifyForHtml, removeAttributesForPrint}, printHeaderTemplate) {

	html.registerDataAttribute("PRINT_BREAK", "print-break", Boolean, true);
	html.registerDataAttribute("PRINT_MARGIN_HEIGHT", "print-mh", Number, true);
	html.registerDataAttribute("PRINT_MARGIN_WIDTH", "print-mw", Number, true);
	html.registerDataAttribute("PRINT_SCALE", "print-scale", Boolean, true);
	html.registerDataAttribute("PRINT_SCALE_MIN", "print-scale-min", Number, true);
	html.registerDataAttribute("PRINT_SCALE_HEIGHT", "print-sh", Number, true);
	html.registerDataAttribute("PRINT_SCALE_WIDTH", "print-sw", Number, true);
	html.registerDataAttribute("PRINT_SPLITTER", "print-splitter", null, true);
	html.registerDataAttribute("PRINT_ZOOM", "print-zoom", Number, true);

	const CSS_PAGE = "csi-print-page";
	const CSS_PAGE_SPACER = "csi-print-page-spacer";
	const CSS_PAGE_NUMBER = "csi-print-page-number";
	const CSS_PAGE_WIDTH = "csi-print-page-width";
	const MM = "mm";

	// CSS unit conversion - absolute units are hard-coded to 96px per inch
	var MM_PER_INCH = 25.4;
	var PX_PER_INCH = 96;
	var PX_PER_MM = PX_PER_INCH / MM_PER_INCH;

	// default PDF settings of Header/Footer Font Size=9, and adding a 4mm margin between header text and content;  multiply by 2 below for header and footer
	// FIX: ideally client should know values of PDF print settings and change calculations accordingly
	var PDF_PT_TO_MM = 0.35277;
	var PDF_PT_FONT_SIZE = 9;
	var PDF_FONT_HEIGHT = Math.floor(PDF_PT_TO_MM * PDF_PT_FONT_SIZE);
	var PDF_MARGIN_HEADER = PDF_FONT_HEIGHT + 4.5;
	var PDF_MARGIN_FOOTER = PDF_FONT_HEIGHT + 2;
	var PDF_MARGIN_HEIGHT = PDF_MARGIN_HEADER + PDF_MARGIN_FOOTER;
	var PDF_MINIMUM_MARGIN = 4;

	// Number: height of header in millimeters + margin
	var PRINT_HEADER_HEIGHT = 10;
	var PRINT_HEADER_MARGIN = 2;

	// Number: height of footer in millimeters
	var PRINT_FOOTER_HEIGHT = 5;

	// Number: height of margin in millimeters for printing (ad-hoc or pdf); we could be more precise for browser printing vs PDF printing,
	// but consistency among the prints has been deemed more important than maximizing space
	var PRINT_MARGIN_HEIGHT = Math.max(PRINT_FOOTER_HEIGHT, PDF_MARGIN_HEIGHT);

	// Object: standard dimensions of paper sizes (measurements are in millimeters and assume portrait orientation)
	var PAPER_SIZES = {
		A3: {w: 297, h: 420},
		A4: {w: 210, h: 297},
		A5: {w: 148, h: 210},
		Letter: {w: 215, h: 279},
		Legal: {w: 215, h: 356},
		Tabloid: {w: 279, h: 432}
	};

	var _adHocPdf;
	var _area;
	var _autoScale;
	var _comments;
	var _commentHeader;
	var _commentPage;
	var _header;
	var _isDatasheet;
	var _orientation;
	var _paper;
	var _pdf;
	var _persistent;
	var _persistentOverride;
	var _root;

	var _pageHeight;
	var _pageWidth;

	var _viewSplitterMap = {};

	// # of rows and columns in the case of talbe splitter
	/** @property {number} _maxTableColumns - the number of columns of the table, or max number of columns of multiple tables */
	var _maxTableColumns = 0;

	/** @property {number} _maxTableRows - the number of rows of the table, or max number of rows of multiple tables */
	var _maxTableRows = 0;

	topic.subscribe("/csi/table/splitter/info", _onSplitTable);

	function _createCommentList(n) {
		return $("<ol/>", {
			"class": "csi-print-comments-list " + html.CSS_RICH_TEXT,
			"start": n || 1
		});
	}

	/**
	 * Put the number of rows/columns information in the DOM body, get the max one in the case of multiple tables
	 * @param {number} rows
	 * @param {number} columns
	 */
	function _onSplitTable(rows, columns) {
		if (_pdf) {
			_maxTableColumns = Math.max(_maxTableColumns, columns);
			_maxTableRows = Math.max(_maxTableRows, rows);
			$("body").attr("csi_rows", _maxTableRows).attr("csi_columns", _maxTableColumns);
		}
	}

	function _init() {
		if (!_root || !_root.length) {
			// create ad-hoc print area
			var domPrint = $("<div/>");
			domPrint.addClass("printBody");

			_root = $("<div/>").appendTo(domPrint);
			_root.addClass("printInnerBody");

			domPrint.appendTo("body");
		}

		if (!_area) {
			// create print area - PDF service uses "printContent" id for HTML stitching, so make sure to keep in place
			_area = $("<div/>", {
				id: "printContent",
				"class": "print"
			}).appendTo(_root);

			// enable special styling per user type
			if (sessionConstants.isLocalUser) {
				_area.addClass(sessionConstants.userType);
			}
		}
	}

	/**
	 * Add comments to appropriate page(s)
	 */
	function _pageComments() {
		// determine if comments fit on last page
		var lastPage = _area.children().last();
		var maxPageHeight = _pageHeight;
		var lastPageHeight = lastPage.height();
		var commentHeight = _commentPage.height();
		var totalHeight = lastPageHeight + commentHeight;

		// if comments all fit on last page, then move there and return
		if (totalHeight <= maxPageHeight) {
			_commentPage.appendTo(lastPage);
			return;
		}

		// if comments fit on their own page, then leave comments on own page
		if (commentHeight <= maxPageHeight) {
			return;
		}

		// split comments across pages
		var headerHeight = _commentHeader.height();
		var heightForComments = maxPageHeight - lastPageHeight - headerHeight;
		var initialCommentsHeight = 0;

		var commentHeights = _comments.children().map(function(i) {
			var h = $(this).height();

			// track height of first one or two comments to find on which page comments should appear;
			// goal is to get at least 2 comments or one higher than 50 pixels on the first page
			if ((i == 0) || ((i == 1) && (initialCommentsHeight < 50))) {
				initialCommentsHeight += h;
			}

			return h;
		});

		// if the header and first few comments fit on lastPage, then add them there
		if ((heightForComments > 50) && (initialCommentsHeight <= heightForComments)) {
			_commentPage.appendTo(lastPage);
		} else {
			heightForComments = maxPageHeight;
		}

		// at this point, comments start on the right page, but there are more than will fit, so need to
		// move those that don't fit to next page
		var commentList;
		var h = 0;

		_comments.children().each(function(i) {
			// if height with next comment exceeds available height, then create another page
			if (h + commentHeights[i] > heightForComments) {
				_commentPage = $("<div/>");
				commentList = _createCommentList(i + 1);
				commentList.appendTo(_commentPage);
				_commentPage.appendTo(_area);
				heightForComments = maxPageHeight;
				h = 0;
			}

			// first time through comments are already in place, so no need to move.
			if (commentList) {
				$(this).appendTo(commentList);
			}

			// track height of comments on page
			h += commentHeights[i];
		});
	}

	function _setPaper(paperSize) {
		var size = getPageSizeForSettings(paperSize, _orientation, !_pdf && !_isDatasheet, !!_header);

		_paper = paperSize;
		_pageHeight = size.h;
		_pageWidth = size.w;

		// PDF service requires this to know what size PDF to generate
		if (_pdf) {
			$("body").attr("csi_paper", _paper).attr("csi_orient", _orientation);
		}

		// set area width to paper width - height is not set to allow for multiple pages
		_area.width(_pageWidth);

		// set height and width state so other modules have easy access to drive layout behavior when printing
		html.setPrintWidth(_pageWidth);
		state.set("printHeight", _pageHeight);
		state.set("printWidth", _pageWidth);
	}

	function _finishContent() {
		// increment width and height by 1 just to make sure it fits any borders in content
		var height = _pageHeight + 1;
		var width = _pageWidth + 1;

		var areaNode = _area.get(0);
		var zoom = api.getPrintZoomForPortraitDataSheets(_pdf);

		// insert comments if they exist
		if (_comments) {
			if (_comments.children().length) {
				_commentPage.appendTo(_area);

				if (!_adHocPdf) {
					_pageComments();
				}
			}

			_comments = null;
		}

		if (_adHocPdf) {
			// if ad-hoc PDF, then insert header so it is in the content sent to the server - it will be extracted
			// and then inserted into each page after being split
			if (_header) {
				_area.prepend(_header);
			}

			// save print settings to content so it can be extracted in fragment.html
			var margins = api.getPrintMargins(_paper, _orientation, true);
			var scaling = api.getPrintScalingForPortrait(true);
			var scaleMin = sessionStore.minimumFontScaling;

			html.setDataAttribute(areaNode, html.PRINT_MARGIN_WIDTH, margins.w);
			html.setDataAttribute(areaNode, html.PRINT_MARGIN_HEIGHT, margins.h);
			html.setDataAttribute(areaNode, html.PRINT_SCALE, _autoScale);
			html.setDataAttribute(areaNode, html.PRINT_SCALE_MIN, scaleMin);
			html.setDataAttribute(areaNode, html.PRINT_SCALE_WIDTH, scaling.w);
			html.setDataAttribute(areaNode, html.PRINT_SCALE_HEIGHT, scaling.h);
			html.setDataAttribute(areaNode, html.PRINT_ZOOM, zoom);
		} else {
			// format pages with headers and footers
			var pages = _area.children();
			var nPages = pages.length;

			// apply zoom factor to content in portrait mode when printing data sheet views to scale them up to paper size
			if (zoom && _isDatasheet && (_orientation == api.PORTRAIT)) {
				_area.css("zoom", zoom);
			}

			pages.each(function(i) {
				const page = this;
				const fullPage = html.create("div", {"class": CSS_PAGE}, page, "after");
				html.setPixels(fullPage, html.WIDTH, width);
				fullPage.appendChild(page);

				// set min-height rather than height so that content can overflow to next page if it cannot be split to fit page (i.e. rich text)
				html.setPixels(page, html.MINHEIGHT, height);

				if (_paper && !_pdf) {
					// add spacer to show page boundaries - this is needed since page can still have too much content once split
					const paperDims = _getPaperSizeForSettings(_paper, _orientation, !_isDatasheet);

					html.create("div", {
						"class": CSS_PAGE_SPACER,
						style: "height: " + paperDims.h + MM
					}, fullPage, "first");

					fullPage.style.minHeight = paperDims.h + MM;

					// add page numbers to footer if printing single view to browser - include page break on all but last page
					html.create("div", {
						"class": CSS_PAGE_NUMBER,
						innerText: locale.resource("PrintPageNumber", [i + 1, nPages])
					}, fullPage);
				}
			});

			// show header before each page if desired
			if (_header) {
				_header.insertBefore(pages);
			}
		}

		// set area width to paper width minus the minimum allowed width in PDF, which appears to be 2mm on each side;
		// the width is necessary for each print page to be centered horizontally; if not PDF, then just set the
		// width the same as used for the pages
		if (_pdf) {
			var size = PAPER_SIZES[_paper];
			var portrait = (_orientation == api.PORTRAIT);
			width = ((portrait ? size.w : size.h) - PDF_MINIMUM_MARGIN) + MM;
		}

		_area.width(width);

		// remove all unnecessary attributes to keep content to a minimum
		removeAttributesForPrint(areaNode);

		// reset states for printing - resetting print state will reset other states (i.e. pdf)
		html.setPrintStatus(false);
		state.set("print", false);

		// remove class indicating print is in progress - add a delay so that we do not remove the class
		// until after the print preview window appears so that content is correct; this class should not
		// be in place when just using the browser (whole page) print functionality
		_.delay(function() {
			$(document.documentElement).removeClass("csi-print-in-progress");
		}, 1000);

		// capture final HTML and clear print area for next print
		var content = _root.html();

		if (!_persistent && !_persistentOverride) {
			html.destroyChildren(areaNode);
		}

		return {
			content: content,
			orientation: _orientation,
			paper: _paper
		};
	}

	/**
	 * Convert millimeters to nearest pixel count
	 * @param {number} mm
	 * @return {number}
	 */
	function convertMMtoPX(mm) {
		return Math.round(mm * PX_PER_MM);
	}

	/**
	 * Return size of print paper in mm given paper size and orientation
	 * @param {string} paper
	 * @param {string} orientation
	 * @param {boolean} [isStandardView] - indicates if this is for printing view in browser only (as opposed to a print view that goes to PDF)
	 * @return {Object} - object with properties h and w measured in mm
	 */
	function _getPaperSizeForSettings(paper, orientation, isStandardView) {
		var bPdf = !isStandardView;
		var margins = api.getPrintMargins(paper, orientation, bPdf);
		var size = PAPER_SIZES[paper];
		var portrait = (orientation == api.PORTRAIT);
		var h = (portrait ? size.h : size.w) - Math.ceil(25.4 * margins.h);
		var w = (portrait ? size.w : size.h) - Math.ceil(25.4 * margins.w);

		// if printing in portrait mode and this is a standard view, then apply configured scaling for current browser;
		// if not a standard view, then the content must be authored in the normal size, and then zoomed when printed
		if (portrait && isStandardView) {
			var scaling = api.getPrintScalingForPortrait(bPdf);
			if (scaling) {
				h *= scaling.h;
				w *= scaling.w;
			}
		}

		return {h: h, w: w};
	}

	/**
	 * Return size of print page in pixels given paper size and orientation and setting for showing page header
	 * @param {string} paper
	 * @param {string} orientation
	 * @param {boolean} [isStandardView] - indicates if this is for printing view in browser only (as opposed to a print view that goes to PDF)
	 * @param {boolean} [showHeader]
	 * @return {Object} - object with properties h and w measured in pixels
	 */
	function getPageSizeForSettings(paper, orientation, isStandardView, showHeader) {
		const dims = _getPaperSizeForSettings(paper, orientation, isStandardView);

		dims.h -= isStandardView ? PRINT_FOOTER_HEIGHT : PRINT_MARGIN_HEIGHT;

		if (showHeader) {
			dims.h -= PRINT_HEADER_HEIGHT + PRINT_HEADER_MARGIN;
		}

		// convert to pixels
		dims.h = Math.ceil(dims.h * PX_PER_MM);
		dims.w = Math.ceil(dims.w * PX_PER_MM);

		return dims;
	}

	/**
	 * @return {number} The width of print content in pixels.
	 */
	function getContentWidth() {
		// the page's width is constrained by the print area, but it is possible that data tables exceed that width and are not reflected in the computed width
		var maxWidth = 0;

		function findMax() {
			maxWidth = Math.max(maxWidth, this.offsetWidth);
		}

		// some elements should not be considered when determining width since they could wrap (i.e. gallery grouping table),
		// so add class to allow for hiding elements
		_area.addClass(CSS_PAGE_WIDTH);

		_area.find("." + html.CSS_VIEW).each(findMax);
		_area.find(".propertyViewTable").each(findMax);

		// for tables that fit on the page, the body matches the page width exactly; due to borders, the table element is 1px wider.
		// so use the body as a judge of the true width rather than the table
		_area.find(".csi-view-table > tbody").each(findMax);

		_area.removeClass(CSS_PAGE_WIDTH);

		return maxWidth;
	}

	/**
	 * Get ratio for a given paper size and orientation (landscape or portrait)
	 * @param {string} paperSize - Options: A3 A4 A5 Legal Letter Tabloid
	 * @param {boolean} portrait - Orientation: Landscape or Portrait
	 * @return {number} Ratio for paper size and orientation, default A4 Landscape
	 */
	function getRatioOfPaperSizeAndOrientation(paperSize, portrait) {
		const {w, h} = PAPER_SIZES[paperSize] || PAPER_SIZES.A4;
		return portrait ? h / w : w / h;
	}

	/**
	 * @param {string} paper
	 * @return {string[]} List of paper sizes with width and height less than or equal to given paper size
	 */
	function getSmallerPaperSizes(paper) {
		var paperDimensions = PAPER_SIZES[paper];

		var validSizes = [];

		_.each(PAPER_SIZES, function(value, key) {
			if (paperDimensions.w >= value.w && paperDimensions.h >= value.h) {
				validSizes.push(key);
			}
		});

		return validSizes;
	}

	/**
	 * Notify print service that the print content is ready.
	 */
	function notifyPrintServiceDone() {
		// delay just to make sure DOM is fully ready before notifying the service
		_.delay(function() {
			document.title = "Done";
		}, 1);
	}

	/**
	 * Notify print service that the print content is empty (used for stitching). Only used with persistent root.
	 */
	function notifyPrintServiceEmpty() {
		html.destroyChildren(_root.get(0));
		_area = null;
		_commentPage = null;
		_commentHeader = null;
		_comments = null;
		_header = null;

		document.title = "Done";
	}

	/**
	 * Notify print service that error was encountered while printing content.
	 * @param {string|Error} [err]
	 */
	function notifyPrintServiceError(err) {
		if (err) {
			var message = err;

			if (typeof err !== "string") {
				message = locale.resource("PrintError_General");

				// only include stack trace if error is unknown
				if (err.stack) {
					$("<div/>", {id: "csiErrorStackTrace"}).text(err.stack).appendTo("body");
				}
			}

			$("body").attr("csi_error", message);
		}

		document.title = "Error";
	}

	/**
	 * Notify print service that printing is in progress.
	 */
	function notifyPrintServiceLoading() {
		document.title = "Loading...";
	}

	/**
	 * Register the splitter class for views with the given className
	 * @param {string} className
	 * @param {Function} splitterClass
	 */
	function registerViewSplitter(className, splitterClass) {
		_viewSplitterMap[className] = splitterClass;
	}

	/**
	 * Reset the counts of rows and columns make sure it can be calculated correctly
	 */
	function resetCounts() {
		_maxTableColumns = 0;
		_maxTableRows = 0;
	}

	/**
	 * Return print page set to the desired paper size and orientation where content can be inserted
	 * @param {boolean} [asPdf=false] - Indicates if printing directly to PDF as opposed to manual printing in browser
	 * @param {boolean} [bPersistent=false] - Indicates if printing content in place as opposed to opening up a separate window
	 * @return {DOMNode} The print page element to which print content can be added.
	 */
	function reservePrintArea(asPdf, bPersistent) {
		// only able to print one at a time
		console.assert(!state.get("print"));

		// reset the counts if needed
		resetCounts();
		_init();

		// reset print area - make sure view widget is cleaned up
		if (_area.length) {
			html.destroyChildren(_area.get(0));
		}

		_pdf = !!asPdf;
		_adHocPdf = _pdf && state.get("printAdHoc");
		_persistentOverride = bPersistent;
		_area.css("font-size", "100%");
		_area.toggleClass("pdf", _pdf);

		// update state for printing
		html.setPrintStatus(true);
		state.set("print", true);

		// add class to body for one-click print use case - we need to blank out all content
		// other than what is in the print area
		$(document.documentElement).addClass("csi-print-in-progress");

		return _area.get(0);
	}

	/**
	 * Return content to print along with paper size and orientation
	 * @return {{content: String, orientation: String, paper: String}} A triple containing the HTML content, as well as paper size and orientation used.
	 */
	function releasePrintArea() {
		// scale and page content if not ad-hoc PDF
		var dfd = new Deferred().resolve();

		if (!_adHocPdf) {
			// For ad-hoc PDF, we need to scale/split the content on the browser that will print the view - not on the browser that invoked the PDF request
			dfd = dfd.then(function() {
				if (_autoScale) {
					return scaleContent();
				}
			}).then(function() {
				var rootNode = _area.children().get(0);
				splitContent([rootNode], rootNode);
			});
		}

		return dfd.then(_finishContent);
	}

	/**
	 * Scale font-size until content fits in print area, or no more change is possible.
	 * @param {Number} [pageWidth] - available width of content (exposed for unit tests)
	 * @param {function} [contentWidthFunc] - Function to call to get the content width (exposed for unit tests)
	 * @param {JQuery} [areasToChangeFont] - areas we want to scale the font on (exposed for unit tests)
	 * @return {Deferred}
	 */
	function scaleContent(pageWidth, contentWidthFunc, areasToChangeFont) {
		pageWidth = pageWidth || _pageWidth;
		contentWidthFunc = contentWidthFunc || getContentWidth;
		areasToChangeFont = areasToChangeFont || _area.find(".propertyViewTable, .csi-view-table");

		var contentWidth = contentWidthFunc();
		var lowerBound = sessionStore.minimumFontScaling;
		var upperBound = 1;
		var finalScale = 1;
		var previousScale;
		var finalDfd;

		// if content width is less than pageWidth then we don't need scaling
		if (Math.floor(pageWidth) < Math.floor(contentWidth)) {
			finalDfd = new Deferred();

			// Our goal is to make sure we fit the print content in single page horizontally.
			// When font scale changes between 80% to 60%, the total width will not change
			// because contents that are in multiple lines will become one line and column width will not change.
			// So we need to force pageWidth on the content.
			// NOTE: _area may be null if running unit tests
			if (_area) {
				_area.find(".tableViewRoot[data-csi-print-splitter] .csi-view-table")
					.filter(i => !$(this).width())
					.each(i => $(this).width(pageWidth));
			}

			function done() {
				state.set("printScale", finalScale);

				// extra timeout here just to make sure browser updates before splitting content;
				// it sometimes works without it, but not always
				setTimeout(function() {
					finalDfd.resolve();
				}, 1);
			}

			function scale() {
				areasToChangeFont.addClass("csi-print-scaled");
				areasToChangeFont.css("font-size", (100 * finalScale) + "%");
			}

			function iterate() {
				previousScale = finalScale;
				finalScale = Math.floor((((upperBound - lowerBound) / 2) + lowerBound) * 100) / 100;

				if (Math.round((upperBound - lowerBound) * 100) == 1) {
					finalScale = lowerBound;
				}

				scale();

				if (finalScale == lowerBound) {
					done();
					return;
				}

				var newWidth = contentWidthFunc();

				if (Math.floor(newWidth) > Math.floor(pageWidth)) {
					// if the percentage was reduced by 10% and no change to content size, then stop
					if ((newWidth === contentWidth) && (Math.abs(finalScale - upperBound) >= 0.1)) {
						finalScale = previousScale;
						scale();
						done();
						return;
					}

					// the content is still too wide, so again reduce scale percentage
					upperBound = finalScale;
				} else {
					lowerBound = finalScale;
				}

				iterate();
			}

			iterate();
		}

		return finalDfd;
	}

	/**
	 * Set print content and settings
	 * @param {string} content - Html content to display for printing
	 * @param {string} paperSize - The paper size used for printing
	 * @param {string} orientation - The paper orientation used for printing
	 */
	function setContent(content, paperSize, orientation) {
		// this is only used for printing on the server to generate a PDF; assume it is a datasheet view as
		// it doesn't really matter when also in PDF mode
		_pdf = true;
		_isDatasheet = true;

		// use destroyChildren to make sure to clean up widgets
		html.destroyChildren(_root.get(0));

		// insert content into DOM and update state
		_root.html(content);
		_area = _root.find("#printContent");

		html.setPrintStatus(true);
		state.set("print", true);
		state.set("printScale", 1);

		// update api/state based on attributes in content
		var areaNode = _area.get(0);
		var mw = html.getDataAttribute(areaNode, html.PRINT_MARGIN_WIDTH);
		var mh = html.getDataAttribute(areaNode, html.PRINT_MARGIN_HEIGHT);
		var sw = html.getDataAttribute(areaNode, html.PRINT_SCALE_WIDTH);
		var sh = html.getDataAttribute(areaNode, html.PRINT_SCALE_HEIGHT);
		var scaleMin = html.getDataAttribute(areaNode, html.PRINT_SCALE_MIN);
		var zoom = html.getDataAttribute(areaNode, html.PRINT_ZOOM);

		_autoScale = html.getDataAttribute(areaNode, html.PRINT_SCALE);

		sessionStore.printAutoScale = _autoScale;
		sessionStore.minimumFontScaling = scaleMin;
		api.setPrintScalingForPortrait(api.PDF, sw, sh);
		api.setPrintZoomForPortraitDataSheets(api.PDF, zoom);

		// margins API will throw error if pass 0 for any of parameters, so add/subtract delta to make parameters non-zero
		api.setPrintMargins(mw + 0.5, -0.5, mh + 0.5, mh - 0.5, orientation, api.PDF, paperSize);

		// extract comments
		_commentPage = _root.find(".csi-print-comments");

		if (_commentPage.length) {
			_commentPage.remove();
			_commentHeader = _commentPage.find(".csi-print-comments-header");
			_comments = _commentPage.find(".csi-print-comments-list");
		} else {
			_commentPage = null;
			_commentHeader = null;
			_comments = null;
		}

		// extract header
		_header = _root.find(".csi-print-header");

		if (_header.length) {
			_header.remove();
		} else {
			_header = null;
		}

		// set paper last as it relies on other settings
		_orientation = orientation;
		_setPaper(paperSize);

		return new Promise(resolve => {
			imagesloaded(_root, resolve);
		});
	}

	/**
	 * Set root selector where print content will be rendered
	 * @param {string} selector - Selector used to query for root node rather than create it
	 */
	function setPersistentRoot(selector) {
		_root = $(selector);
		_persistent = true;
	}

	/**
	 * Set options for print including paper size, orientation, showing comments, and showing header with breadcrumb text and view name
	 * @param {string} paperSize - The paper size used for printing with possible values of "A3", "A4", "A5", "Letter", "Legal", "Tabloid", or undefined if printing whole screen
	 * @param {string} orientation - The orientation used for printing, either "Portrait", "Landscape", or undefined if printing whole screen
	 * @param {boolean} autoScale - Indicates if view content should be scaled automatically to fit better on page
	 * @param {boolean} showComments - Indicates if comments should be displayed for attributes in the view
	 * @param {string} [breadcrumb] - Text of Breadcrumb trail to current node
	 * @param {string} [viewName] - Name of view to print
	 * @param {string} [isDatasheet] - indicates if view is specially for printing, as opposed to single standard view; this affects margins that are allocated for printing
	 */
	function setPrintOptions(paperSize, orientation, autoScale, showComments, breadcrumb, viewName, isDatasheet) {
		// if showing comments, then setup list where they will be rendered
		if (breadcrumb) {
			var headerParams = {
				breadcrumb: breadcrumb,
				logo: getLogo(),
				view: viewName || ""
			};

			_header = $(html.stringToDom(printHeaderTemplate, headerParams))
				.height(PRINT_HEADER_HEIGHT + MM)
				.css("margin-bottom", PRINT_HEADER_MARGIN + MM);
		} else {
			_header = null;
		}

		if (showComments) {
			_commentPage = $("<div/>", {"class": "csi-print-comments"});
			_commentHeader = $("<div/>", {"class": "csi-print-comments-header"})
				.text(locale.resource("PrintComments"))
				.appendTo(_commentPage);

			_comments = _createCommentList();
			_comments.appendTo(_commentPage);
		} else {
			_comments = null;
		}

		_autoScale = autoScale;
		_isDatasheet = isDatasheet;
		_orientation = orientation;
		_setPaper(paperSize);

		state.set("printAutoScale", _autoScale);
		state.set("printScale", 1);
	}

	/**
	 * Split print content into pages
	 * @param {DOMNode[]} pages
	 * @param {DOMNode} rootNode
	 * @param {Function} [copyCallback] - function called when child view is copied from one page to another
	 */
	function splitContent(pages, rootNode, copyCallback) {
		var splitterType = html.getDataAttribute(rootNode, html.PRINT_SPLITTER);
		var splitterClass = _viewSplitterMap[splitterType];
		if (splitterClass) {
			var sp = new splitterClass();
			sp.split(pages, rootNode, state.get("printHeight"), state.get("printWidth"), copyCallback);
		}
	}

	/**
	 * @return {boolean} - Returns true if currently printing for PDF generation. False if it's only for print preview.
	 */
	function isPdf() {
		return _pdf;
	}

	aspect.around(csiHeading, "toComment", function(originalFunction) {
		return function(ctx, refNode, value) {
			if (_comments) {
				// add span with index to reference comment
				$("<span/>", {"class": "csi-print-comment-number"})
					.text(_comments.children().length + 1)
					.appendTo(refNode);

				// add comment itself
				$("<li/>")
					.html(purifyForHtml(value))
					.appendTo(_comments);
			} else {
				return originalFunction.apply(this, arguments);
			}
		};
	});

	return {
		convertMMtoPX,
		getContentWidth,
		getPageSizeForSettings,
		getRatioOfPaperSizeAndOrientation,
		getSmallerPaperSizes,
		isPdf,
		notifyPrintServiceDone,
		notifyPrintServiceEmpty,
		notifyPrintServiceError,
		notifyPrintServiceLoading,
		registerViewSplitter,
		releasePrintArea,
		reservePrintArea,
		scaleContent,
		splitContent,
		setContent,
		setPersistentRoot,
		setPrintOptions
	};
});
